import React, { useMemo, useState, useEffect } from 'react';

function TelematicSummary({ data, driverName, dateRange }) {
  const [globalStats, setGlobalStats] = useState(null);
  const [initialData, setInitialData] = useState(null);

  // Fonction pour calculer les stats pour un ensemble de données
  const calculateStats = (tripData) => {
    if (!tripData || tripData.length === 0) return null;

    // Grouper les trajets par conducteur puis par date
    const tripsByDriverAndDate = tripData.reduce((acc, trip) => {
      const driverName = trip.DRIVER_NAME;
      const departureDate = new Date(trip.DEPARTURE_DATETIME);

      // Si le trajet commence entre minuit et 4h du matin, on le considère comme faisant partie de la veille
      if (departureDate.getHours() >= 0 && departureDate.getHours() < 4) {
        departureDate.setDate(departureDate.getDate() - 1);
      }

      const date = departureDate.toISOString().split('T')[0];

      if (!acc[driverName]) acc[driverName] = {};
      if (!acc[driverName][date]) acc[driverName][date] = [];

      acc[driverName][date].push({
        ...trip,
        adjustedHour: departureDate.getHours() >= 0 && departureDate.getHours() < 4
          ? departureDate.getHours() + 24
          : departureDate.getHours()
      });
      return acc;
    }, {});

    // Pour chaque conducteur et chaque date, ne garder que le premier départ
    const firstTripsOfDay = [];
    const lastTripsOfDay = [];
    Object.values(tripsByDriverAndDate).forEach(driverDates => {
      Object.values(driverDates).forEach(dayTrips => {
        const sortedTrips = dayTrips.sort((a, b) => {
          const timeA = a.adjustedHour * 60 + new Date(a.DEPARTURE_DATETIME).getMinutes();
          const timeB = b.adjustedHour * 60 + new Date(b.DEPARTURE_DATETIME).getMinutes();
          return timeA - timeB;
        });
        firstTripsOfDay.push(sortedTrips[0]);
        lastTripsOfDay.push(sortedTrips[sortedTrips.length - 1]);
      });
    });

    const departureTimes = firstTripsOfDay.map(trip => {
      return trip.adjustedHour * 60 + new Date(trip.DEPARTURE_DATETIME).getMinutes();
    });

    const arrivalTimes = lastTripsOfDay.map(trip => {
      const arrivalDate = new Date(trip.ARRIVAL_DATETIME);
      const adjustedHour = arrivalDate.getHours() >= 0 && arrivalDate.getHours() < 4
        ? arrivalDate.getHours() + 24
        : arrivalDate.getHours();
      return adjustedHour * 60 + arrivalDate.getMinutes();
    });

    const tripsPerDay = Object.values(tripsByDriverAndDate).map(driverDates =>
      Object.values(driverDates).map(dayTrips => dayTrips.length)
    ).flat();

    const tripDurations = tripData.map(trip => {
      const [hours, minutes, seconds] = trip.TRIP_TIME.split(':').map(Number);
      return hours * 60 + minutes + (seconds / 60);
    });

    // Extraire les distances des trajets
    const tripDistances = tripData.map(trip => trip.DISTANCE_TRAVELED);

    // Pour chaque date, calculer le temps total et l'amplitude
    const dailyStats = Object.values(tripsByDriverAndDate).map(driverDates => {
      return Object.entries(driverDates).map(([date, dayTrips]) => {
        const sortedTrips = dayTrips.sort((a, b) => {
          const timeA = a.adjustedHour * 60 + new Date(a.DEPARTURE_DATETIME).getMinutes();
          const timeB = b.adjustedHour * 60 + new Date(b.DEPARTURE_DATETIME).getMinutes();
          return timeA - timeB;
        });

        // Calculer le temps total dans le véhicule
        const totalVehicleTime = dayTrips.reduce((total, trip) => {
          const [hours, minutes, seconds] = trip.TRIP_TIME.split(':').map(Number);
          return total + (hours * 60 + minutes + seconds / 60);
        }, 0);

        // Calculer l'amplitude (temps entre premier départ et dernière arrivée)
        const firstTrip = sortedTrips[0];
        const lastTrip = sortedTrips[sortedTrips.length - 1];

        const firstDeparture = firstTrip.adjustedHour * 60 + new Date(firstTrip.DEPARTURE_DATETIME).getMinutes();

        const lastArrival = new Date(lastTrip.ARRIVAL_DATETIME);
        const lastArrivalHour = lastArrival.getHours() >= 0 && lastArrival.getHours() < 4
          ? lastArrival.getHours() + 24
          : lastArrival.getHours();
        const lastArrivalTime = lastArrivalHour * 60 + lastArrival.getMinutes();

        const amplitude = lastArrivalTime - firstDeparture;

        // Calculer la durée du premier et dernier trajet
        const firstTripDuration = (() => {
          const [hours, minutes, seconds] = firstTrip.TRIP_TIME.split(':').map(Number);
          return hours * 60 + minutes + (seconds / 60);
        })();

        const lastTripDuration = (() => {
          const [hours, minutes, seconds] = lastTrip.TRIP_TIME.split(':').map(Number);
          return hours * 60 + minutes + (seconds / 60);
        })();

        return {
          totalVehicleTime,
          amplitude,
          firstTripDuration,
          lastTripDuration
        };
      }).flat();
    }).flat();

    const calculateAverage = (numbers) => numbers.reduce((a, b) => a + b, 0) / numbers.length;

    return {
      departures: {
        average: calculateAverage(departureTimes),
        earliest: Math.min(...departureTimes),
        latest: Math.max(...departureTimes)
      },
      arrivals: {
        average: calculateAverage(arrivalTimes),
        earliest: Math.min(...arrivalTimes),
        latest: Math.max(...arrivalTimes)
      },
      tripsPerDay: {
        average: calculateAverage(tripsPerDay),
        minimum: Math.min(...tripsPerDay),
        maximum: Math.max(...tripsPerDay)
      },
      durations: {
        average: calculateAverage(tripDurations),
        minimum: Math.min(...tripDurations),
        maximum: Math.max(...tripDurations)
      },
      distances: {
        average: calculateAverage(tripDistances),
        minimum: Math.min(...tripDistances),
        maximum: Math.max(...tripDistances)
      },
      totalVehicleTime: {
        average: calculateAverage(dailyStats.map(stat => stat.totalVehicleTime)),
        minimum: Math.min(...dailyStats.map(stat => stat.totalVehicleTime)),
        maximum: Math.max(...dailyStats.map(stat => stat.totalVehicleTime))
      },
      amplitude: {
        average: calculateAverage(dailyStats.map(stat => stat.amplitude)),
        minimum: Math.min(...dailyStats.map(stat => stat.amplitude)),
        maximum: Math.max(...dailyStats.map(stat => stat.amplitude))
      },
      firstTripDuration: {
        average: calculateAverage(dailyStats.map(stat => stat.firstTripDuration)),
        minimum: Math.min(...dailyStats.map(stat => stat.firstTripDuration)),
        maximum: Math.max(...dailyStats.map(stat => stat.firstTripDuration))
      },
      lastTripDuration: {
        average: calculateAverage(dailyStats.map(stat => stat.lastTripDuration)),
        minimum: Math.min(...dailyStats.map(stat => stat.lastTripDuration)),
        maximum: Math.max(...dailyStats.map(stat => stat.lastTripDuration))
      }
    };
  };

  // Stocker les données initiales non filtrées
  useEffect(() => {
    if (data && data.length > 0 && !initialData) {
      setInitialData(data);
      setGlobalStats(calculateStats(data));
    }
  }, [data, initialData]);

  // Calculer les stats du conducteur sélectionné
  const driverStats = useMemo(() => {
    if (!driverName || !data) return null;
    const filteredData = data.filter(trip => trip.DRIVER_NAME === driverName);
    return calculateStats(filteredData);
  }, [data, driverName]);

  const formatTime = (minutes) => {
    if (!minutes && minutes !== 0) return '--:--';
    const hours = Math.floor(minutes / 60);
    const mins = Math.floor(minutes % 60);
    return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;
  };

  const calculatePercentage = (driverValue, globalValue) => {
    if (!driverValue || !globalValue) return null;

    console.log('Driver value:', driverValue);
    console.log('Global value:', globalValue);

    const diff = ((driverValue - globalValue) / globalValue) * 100;
    console.log('Calculated difference:', diff);

    return diff > 0 ? `+${diff.toFixed(1)}%` : `${diff.toFixed(1)}%`;
  };

  const stats = useMemo(() => {
    if (!globalStats) return null;

    console.log('Global stats:', globalStats);
    console.log('Driver stats:', driverStats);

    // Calculer d'abord les comparaisons sur les valeurs brutes
    const comparisons = driverStats ? {
      departures: calculatePercentage(
        driverStats.departures.average,
        globalStats.departures.average
      ),
      arrivals: calculatePercentage(
        driverStats.arrivals.average,
        globalStats.arrivals.average
      ),
      tripsPerDay: calculatePercentage(
        driverStats.tripsPerDay.average,
        globalStats.tripsPerDay.average
      ),
      durations: calculatePercentage(
        driverStats.durations.average,
        globalStats.durations.average
      ),
      distances: calculatePercentage(
        driverStats.distances.average,
        globalStats.distances.average
      ),
      totalVehicleTime: calculatePercentage(
        driverStats.totalVehicleTime.average,
        globalStats.totalVehicleTime.average
      ),
      amplitude: calculatePercentage(
        driverStats.amplitude.average,
        globalStats.amplitude.average
      ),
      firstTripDuration: calculatePercentage(
        driverStats.firstTripDuration.average,
        globalStats.firstTripDuration.average
      ),
      lastTripDuration: calculatePercentage(
        driverStats.lastTripDuration.average,
        globalStats.lastTripDuration.average
      )
    } : null;

    console.log('Calculated comparisons:', comparisons);

    // Ensuite formater les valeurs pour l'affichage
    const result = {
      departures: {
        average: driverStats ? formatTime(driverStats.departures.average) : formatTime(globalStats.departures.average),
        earliest: driverStats ? formatTime(driverStats.departures.earliest) : formatTime(globalStats.departures.earliest),
        latest: driverStats ? formatTime(driverStats.departures.latest) : formatTime(globalStats.departures.latest),
        comparison: comparisons?.departures
      },
      arrivals: {
        average: driverStats ? formatTime(driverStats.arrivals.average) : formatTime(globalStats.arrivals.average),
        earliest: driverStats ? formatTime(driverStats.arrivals.earliest) : formatTime(globalStats.arrivals.earliest),
        latest: driverStats ? formatTime(driverStats.arrivals.latest) : formatTime(globalStats.arrivals.latest),
        comparison: comparisons?.arrivals
      },
      tripsPerDay: {
        average: driverStats ? driverStats.tripsPerDay.average.toFixed(1) : globalStats.tripsPerDay.average.toFixed(1),
        minimum: driverStats ? driverStats.tripsPerDay.minimum : globalStats.tripsPerDay.minimum,
        maximum: driverStats ? driverStats.tripsPerDay.maximum : globalStats.tripsPerDay.maximum,
        comparison: comparisons?.tripsPerDay
      },
      durations: {
        average: driverStats ? formatTime(driverStats.durations.average) : formatTime(globalStats.durations.average),
        minimum: driverStats ? formatTime(driverStats.durations.minimum) : formatTime(globalStats.durations.minimum),
        maximum: driverStats ? formatTime(driverStats.durations.maximum) : formatTime(globalStats.durations.maximum),
        comparison: comparisons?.durations
      },
      distances: {
        average: driverStats ? driverStats.distances.average.toFixed(1) : globalStats.distances.average.toFixed(1),
        minimum: driverStats ? driverStats.distances.minimum.toFixed(1) : globalStats.distances.minimum.toFixed(1),
        maximum: driverStats ? driverStats.distances.maximum.toFixed(1) : globalStats.distances.maximum.toFixed(1),
        comparison: comparisons?.distances
      },
      totalVehicleTime: {
        average: driverStats ? formatTime(driverStats.totalVehicleTime.average) : formatTime(globalStats.totalVehicleTime.average),
        minimum: driverStats ? formatTime(driverStats.totalVehicleTime.minimum) : formatTime(globalStats.totalVehicleTime.minimum),
        maximum: driverStats ? formatTime(driverStats.totalVehicleTime.maximum) : formatTime(globalStats.totalVehicleTime.maximum),
        comparison: comparisons?.totalVehicleTime
      },
      amplitude: {
        average: driverStats ? formatTime(driverStats.amplitude.average) : formatTime(globalStats.amplitude.average),
        minimum: driverStats ? formatTime(driverStats.amplitude.minimum) : formatTime(globalStats.amplitude.minimum),
        maximum: driverStats ? formatTime(driverStats.amplitude.maximum) : formatTime(globalStats.amplitude.maximum),
        comparison: comparisons?.amplitude
      },
      firstTripDuration: {
        average: driverStats ? formatTime(driverStats.firstTripDuration.average) : formatTime(globalStats.firstTripDuration.average),
        minimum: driverStats ? formatTime(driverStats.firstTripDuration.minimum) : formatTime(globalStats.firstTripDuration.minimum),
        maximum: driverStats ? formatTime(driverStats.firstTripDuration.maximum) : formatTime(globalStats.firstTripDuration.maximum),
        comparison: comparisons?.firstTripDuration
      },
      lastTripDuration: {
        average: driverStats ? formatTime(driverStats.lastTripDuration.average) : formatTime(globalStats.lastTripDuration.average),
        minimum: driverStats ? formatTime(driverStats.lastTripDuration.minimum) : formatTime(globalStats.lastTripDuration.minimum),
        maximum: driverStats ? formatTime(driverStats.lastTripDuration.maximum) : formatTime(globalStats.lastTripDuration.maximum),
        comparison: comparisons?.lastTripDuration
      }
    };

    return result;
  }, [globalStats, driverStats]);

  if (!stats) {
    return (
      <div className="text-center text-gray-600 dark:text-gray-400">
        Aucune donnée disponible pour le calcul des statistiques.
      </div>
    );
  }

  const ComparisonTag = ({ value, isTime = false }) => {
    if (!value) return null;
    const isPositive = value.startsWith('+');
    const colorClass = isTime
      ? (isPositive ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400')
      : (isPositive ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400');

    return (
      <span className={`ml-2 text-xs font-medium ${colorClass}`}>
        ({value})
      </span>
    );
  };

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div className="mb-6">
        <h2 className="text-lg font-semibold text-gray-800 dark:text-white">
          {driverName ? `Statistiques pour ${driverName}` : 'Statistiques globales'}
        </h2>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Période: {dateRange}
        </p>
      </div>

      {/* Grille de statistiques */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {/* Heures de départ */}
        <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
          <h3 className="text-sm font-medium text-blue-700 dark:text-blue-300 mb-2">
            Heures de départ
          </h3>
          <div className="space-y-1">
            <p className="text-sm text-blue-800 dark:text-blue-200">
              Moyenne: {stats.departures.average}
              <ComparisonTag value={stats.departures.comparison} isTime={true} />
            </p>
            <p className="text-sm text-blue-800 dark:text-blue-200">
              Plus tôt: {stats.departures.earliest}
            </p>
            <p className="text-sm text-blue-800 dark:text-blue-200">
              Plus tard: {stats.departures.latest}
            </p>
          </div>
        </div>

        {/* Heures d'arrivée */}
        <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg">
          <h3 className="text-sm font-medium text-green-700 dark:text-green-300 mb-2">
            Heures d'arrivée
          </h3>
          <div className="space-y-1">
            <p className="text-sm text-green-800 dark:text-green-200">
              Moyenne: {stats.arrivals.average}
              <ComparisonTag value={stats.arrivals.comparison} isTime={true} />
            </p>
            <p className="text-sm text-green-800 dark:text-green-200">
              Plus tôt: {stats.arrivals.earliest}
            </p>
            <p className="text-sm text-green-800 dark:text-green-200">
              Plus tard: {stats.arrivals.latest}
            </p>
          </div>
        </div>

        {/* Nombre de trajets par jour */}
        <div className="bg-amber-50 dark:bg-amber-900/20 p-4 rounded-lg">
          <h3 className="text-sm font-medium text-amber-700 dark:text-amber-300 mb-2">
            Trajets par jour
          </h3>
          <div className="space-y-1">
            <p className="text-sm text-amber-800 dark:text-amber-200">
              Moyenne: {stats.tripsPerDay.average}
              <ComparisonTag value={stats.tripsPerDay.comparison} />
            </p>
            <p className="text-sm text-amber-800 dark:text-amber-200">
              Minimum: {stats.tripsPerDay.minimum}
            </p>
            <p className="text-sm text-amber-800 dark:text-amber-200">
              Maximum: {stats.tripsPerDay.maximum}
            </p>
          </div>
        </div>

        {/* Durées de trajet */}
        <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg">
          <h3 className="text-sm font-medium text-purple-700 dark:text-purple-300 mb-2">
            Durées de trajet
          </h3>
          <div className="space-y-1">
            <p className="text-sm text-purple-800 dark:text-purple-200">
              Moyenne: {stats.durations.average}
              <ComparisonTag value={stats.durations.comparison} isTime={true} />
            </p>
            <p className="text-sm text-purple-800 dark:text-purple-200">
              Minimum: {stats.durations.minimum}
            </p>
            <p className="text-sm text-purple-800 dark:text-purple-200">
              Maximum: {stats.durations.maximum}
            </p>
          </div>
        </div>

        {/* Temps total dans le véhicule */}
        <div className="bg-teal-50 dark:bg-teal-900/20 p-4 rounded-lg">
          <h3 className="text-sm font-medium text-teal-700 dark:text-teal-300 mb-2">
            Temps total dans le véhicule
          </h3>
          <div className="space-y-1">
            <p className="text-sm text-teal-800 dark:text-teal-200">
              Moyenne: {stats.totalVehicleTime.average}
              <ComparisonTag value={stats.totalVehicleTime.comparison} isTime={true} />
            </p>
            <p className="text-sm text-teal-800 dark:text-teal-200">
              Minimum: {stats.totalVehicleTime.minimum}
            </p>
            <p className="text-sm text-teal-800 dark:text-teal-200">
              Maximum: {stats.totalVehicleTime.maximum}
            </p>
          </div>
        </div>

        {/* Amplitude horaire */}
        <div className="bg-rose-50 dark:bg-rose-900/20 p-4 rounded-lg">
          <h3 className="text-sm font-medium text-rose-700 dark:text-rose-300 mb-2">
            Amplitude horaire
          </h3>
          <div className="space-y-1">
            <p className="text-sm text-rose-800 dark:text-rose-200">
              Moyenne: {stats.amplitude.average}
              <ComparisonTag value={stats.amplitude.comparison} isTime={true} />
            </p>
            <p className="text-sm text-rose-800 dark:text-rose-200">
              Minimum: {stats.amplitude.minimum}
            </p>
            <p className="text-sm text-rose-800 dark:text-rose-200">
              Maximum: {stats.amplitude.maximum}
            </p>
          </div>
        </div>

        {/* Premier trajet */}
        <div className="bg-indigo-50 dark:bg-indigo-900/20 p-4 rounded-lg">
          <h3 className="text-sm font-medium text-indigo-700 dark:text-indigo-300 mb-2">
            Premier trajet
          </h3>
          <div className="space-y-1">
            <p className="text-sm text-indigo-800 dark:text-indigo-200">
              Moyenne: {stats.firstTripDuration.average}
              <ComparisonTag value={stats.firstTripDuration.comparison} isTime={true} />
            </p>
            <p className="text-sm text-indigo-800 dark:text-indigo-200">
              Minimum: {stats.firstTripDuration.minimum}
            </p>
            <p className="text-sm text-indigo-800 dark:text-indigo-200">
              Maximum: {stats.firstTripDuration.maximum}
            </p>
          </div>
        </div>

        {/* Dernier trajet */}
        <div className="bg-pink-50 dark:bg-pink-900/20 p-4 rounded-lg">
          <h3 className="text-sm font-medium text-pink-700 dark:text-pink-300 mb-2">
            Dernier trajet
          </h3>
          <div className="space-y-1">
            <p className="text-sm text-pink-800 dark:text-pink-200">
              Moyenne: {stats.lastTripDuration.average}
              <ComparisonTag value={stats.lastTripDuration.comparison} isTime={true} />
            </p>
            <p className="text-sm text-pink-800 dark:text-pink-200">
              Minimum: {stats.lastTripDuration.minimum}
            </p>
            <p className="text-sm text-pink-800 dark:text-pink-200">
              Maximum: {stats.lastTripDuration.maximum}
            </p>
          </div>
        </div>

        {/* Distance parcourue */}
        <div className="bg-cyan-50 dark:bg-cyan-900/20 p-4 rounded-lg">
          <h3 className="text-sm font-medium text-cyan-700 dark:text-cyan-300 mb-2">
            Distance parcourue (km)
          </h3>
          <div className="space-y-1">
            <p className="text-sm text-cyan-800 dark:text-cyan-200">
              Moyenne: {stats.distances.average}
              <ComparisonTag value={stats.distances.comparison} />
            </p>
            <p className="text-sm text-cyan-800 dark:text-cyan-200">
              Minimum: {stats.distances.minimum}
            </p>
            <p className="text-sm text-cyan-800 dark:text-cyan-200">
              Maximum: {stats.distances.maximum}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default TelematicSummary;